import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { Link as RouterLink } from 'react-router-dom';
import { FirmDailyNavCompanyConfigRow } from 'api';
import { companyDailyNavSettingsUrl, firmDailyNavSettingsUrl } from 'common/config/urls';
import {
  DAILY_NAV_APPLY_BUTTON_LABEL,
  DAILY_NAV_THRESHOLD_KEYS,
  dailyNavApplyDatesLabel,
  dailyNavApplyDisabledReason,
  dailyNavApplyRefusalMessage,
  dailyNavDateLabel,
  dailyNavNextDayLabel,
  DailyNavThresholdKey,
  fractionToPercent,
  getDailyNavThresholdGroups,
  isValidThresholdPercent,
  percentToFraction,
  selectDailyNavApplyDates,
} from 'common/constants/dailyNav';
import { DAILY_NAV_COMPANIES_PAGE_TITLE, DAILY_NAV_COMPANIES_TAB, FIRM_SETTINGS } from 'common/constants/firms';
import { useFirmScopedPermissions } from 'common/hooks';
import { useStore } from 'common/store';
import type { UseStoreValues } from 'common/types/store';
import AlexandriaCompanyPicker from 'components/AlexandriaCompanyPicker';
import { GridSkeleton } from 'components/Grid';
import { useSecondaryNavigationRegistration, useTertiaryNavigationRegistration } from 'context/FirmBaseLayoutContext';
import {
  buildDailyNavTertiaryNavigationItems,
  buildFirmSettingsNavigationItems,
} from 'pages/Firms/firmSettingsNavigation';
import DailyNavSetupBanner from 'pages/Firms/pages/DailyNavSetup/components/DailyNavSetupBanner';
import {
  useApplyDailyNavConfig,
  useGetDailyNavConfigApplyPreview,
  useGetFirmDailyNavCompanyConfigs,
  useUpdateFirmDailyNavCompanyConfigs,
} from 'services/hooks/dailyNav';
import { DAILY_NAV_COMPANIES_TEST_IDS } from './DailyNavCompanies.testIds';

type ThresholdDraft = Record<DailyNavThresholdKey, string>;

type CompanyDraft = {
  companyId: number;
  companyName: string;
  companySlug: string;
  companyConfigId: number | null;
  monitoringEnabled: boolean;
  thresholds: ThresholdDraft;
  alexandriaId: string | null;
  appliedNavDate: string | null;
  appliedCompanyConfigId: number | null;
  canApply: boolean;
  applyBlockedReason: string | null;
  applySessionClose: string | null;
};

type ApplyDialogState = {
  versionId: number | null;
  companyId?: number;
  companyName?: string;
};

const THRESHOLD_COLUMN_LABELS: Record<DailyNavThresholdKey, string> = {
  cap_iq_short_window_delta: 'CIQ short',
  cap_iq_long_window_delta: 'CIQ long',
  cap_iq_since_last_valuation_delta: 'CIQ since val',
  secondary_short_window_delta: 'Sec short',
  secondary_long_window_delta: 'Sec long',
  secondary_since_last_valuation_delta: 'Sec since val',
  secondary_change_from_mark_delta: 'vs Mark',
};

const emptyThresholds = (): ThresholdDraft =>
  Object.fromEntries(DAILY_NAV_THRESHOLD_KEYS.map(key => [key, ''])) as ThresholdDraft;

const rowToDraft = (row: FirmDailyNavCompanyConfigRow): CompanyDraft => ({
  companyId: row.company_id,
  companyName: row.company_name ?? '',
  companySlug: row.company_slug ?? '',
  companyConfigId: row.company_config_id ?? null,
  monitoringEnabled: row.monitoring_enabled,
  thresholds: Object.fromEntries(
    DAILY_NAV_THRESHOLD_KEYS.map(key => [key, fractionToPercent(row[key])])
  ) as ThresholdDraft,
  alexandriaId: row.alexandria_id ?? null,
  appliedNavDate: row.applied_nav_date ?? null,
  appliedCompanyConfigId: row.applied_company_config_id ?? null,
  canApply: Boolean(row.can_apply),
  applyBlockedReason: row.apply_blocked_reason ?? null,
  applySessionClose: row.apply_session_close ?? null,
});

const draftsEqual = (left: CompanyDraft, right: CompanyDraft) =>
  left.monitoringEnabled === right.monitoringEnabled &&
  left.alexandriaId === right.alexandriaId &&
  DAILY_NAV_THRESHOLD_KEYS.every(key => left.thresholds[key] === right.thresholds[key]);

const thresholdPayload = (draft: CompanyDraft, key: DailyNavThresholdKey) =>
  draft.thresholds[key] === '' ? null : percentToFraction(draft.thresholds[key]);

const draftToRowPayload = (draft: CompanyDraft): FirmDailyNavCompanyConfigRow => ({
  company_id: draft.companyId,
  monitoring_enabled: draft.monitoringEnabled,
  alexandria_id: draft.alexandriaId,
  cap_iq_short_window_delta: thresholdPayload(draft, 'cap_iq_short_window_delta'),
  cap_iq_long_window_delta: thresholdPayload(draft, 'cap_iq_long_window_delta'),
  cap_iq_since_last_valuation_delta: thresholdPayload(draft, 'cap_iq_since_last_valuation_delta'),
  secondary_short_window_delta: thresholdPayload(draft, 'secondary_short_window_delta'),
  secondary_long_window_delta: thresholdPayload(draft, 'secondary_long_window_delta'),
  secondary_since_last_valuation_delta: thresholdPayload(draft, 'secondary_since_last_valuation_delta'),
  secondary_change_from_mark_delta: thresholdPayload(draft, 'secondary_change_from_mark_delta'),
});

const isOpenConfigCurrent = (draft: CompanyDraft) =>
  draft.companyConfigId != null &&
  draft.appliedCompanyConfigId != null &&
  draft.appliedCompanyConfigId === draft.companyConfigId;

const navDayChipColor = (draft: CompanyDraft): 'default' | 'success' | 'warning' => {
  if (!draft.appliedNavDate) {
    return 'default';
  }
  return isOpenConfigCurrent(draft) ? 'success' : 'warning';
};

const navDayTooltip = (draft: CompanyDraft, nextNavDate?: string | null) => {
  if (!draft.appliedNavDate) {
    return 'No NAV day has been generated for this company.';
  }
  if (isOpenConfigCurrent(draft)) {
    return `Current — ${dailyNavDateLabel(draft.appliedNavDate)} NAV and the next`;
  }
  return `${dailyNavDateLabel(draft.appliedNavDate)} still uses previous settings. ${dailyNavNextDayLabel(nextNavDate)}.`;
};

const CompanyNavDayCell = ({
  draft,
  nextNavDate,
  showApply,
  applyDisabled,
  applyDisabledReason,
  onApply,
}: {
  draft: CompanyDraft;
  nextNavDate?: string | null;
  showApply: boolean;
  applyDisabled: boolean;
  applyDisabledReason: string;
  onApply: () => void;
}) => {
  const applyTitle = applyDisabledReason || DAILY_NAV_APPLY_BUTTON_LABEL;
  return (
    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ whiteSpace: 'nowrap' }}>
      <Tooltip title={navDayTooltip(draft, nextNavDate)}>
        <Chip
          size="small"
          color={navDayChipColor(draft)}
          variant={draft.appliedNavDate ? 'filled' : 'outlined'}
          label={draft.appliedNavDate ? dailyNavDateLabel(draft.appliedNavDate) : 'None'}
          sx={{ height: 22, '& .MuiChip-label': { px: 0.75, fontSize: 12 } }}
          data-testid={DAILY_NAV_COMPANIES_TEST_IDS.navDayChip(draft.companyId)}
        />
      </Tooltip>
      {showApply && (
        <Tooltip title={applyTitle} describeChild>
          <span
            data-testid={DAILY_NAV_COMPANIES_TEST_IDS.applyTooltip(draft.companyId)}
            aria-label={applyTitle || undefined}>
            <Button
              size="small"
              variant="text"
              disabled={applyDisabled}
              onClick={onApply}
              sx={{ minWidth: 0, px: 0.5, py: 0 }}
              data-testid={DAILY_NAV_COMPANIES_TEST_IDS.applyButton(draft.companyId)}>
              Apply
            </Button>
          </span>
        </Tooltip>
      )}
    </Stack>
  );
};

const DailyNavCompanies = () => {
  const [{ firmInfo }] = useStore() as unknown as UseStoreValues;
  const { enqueueSnackbar } = useSnackbar();

  const firmId = firmInfo?.id ?? null;
  const firmSlug = firmInfo?.slug ?? '';

  // Daily NAV writes are gated on firm EDIT server-side; this hook is the matching client gate.
  const { canMutateFirmScopedEntities: hasFirmEditAccess } = useFirmScopedPermissions({ firmId });
  const { data, isLoading } = useGetFirmDailyNavCompanyConfigs(firmId);
  const { updateFirmDailyNavCompanyConfigs, isPending } = useUpdateFirmDailyNavCompanyConfigs();
  const {
    data: applyPreview,
    isLoading: isApplyPreviewLoading,
    refetch: refetchApplyPreview,
  } = useGetDailyNavConfigApplyPreview(firmId);
  const { applyDailyNavConfig, isPending: isApplyPending } = useApplyDailyNavConfig();

  const [drafts, setDrafts] = useState<CompanyDraft[]>([]);
  const [savedDrafts, setSavedDrafts] = useState<CompanyDraft[]>([]);
  const [alexandriaCompanyId, setAlexandriaCompanyId] = useState<number | null>(null);
  const [applyDialog, setApplyDialog] = useState<ApplyDialogState | null>(null);

  const firmConfig = data?.firm_config ?? null;
  const hasSavedFirmConfig = Boolean(firmConfig?.id);
  const canEdit = hasFirmEditAccess && hasSavedFirmConfig;
  const applyVersionId = applyDialog?.versionId ?? null;

  const thresholdGroups = useMemo(
    () => getDailyNavThresholdGroups(firmConfig?.short_window_sessions, firmConfig?.long_window_sessions),
    [firmConfig?.short_window_sessions, firmConfig?.long_window_sessions]
  );

  useEffect(() => {
    const nextDrafts = (data?.companies ?? []).map(rowToDraft);
    setDrafts(nextDrafts);
    setSavedDrafts(nextDrafts);
  }, [data]);

  const { applicableDates, recordCount } = useMemo(
    () => selectDailyNavApplyDates(applyPreview?.dates),
    [applyPreview?.dates]
  );
  const canApplyNow = applicableDates.length > 0 && recordCount > 0;
  const applyUnavailableReason = useMemo(() => dailyNavApplyRefusalMessage(applyPreview?.dates), [applyPreview?.dates]);
  const nextNavDate = applyPreview?.next_nav_date ?? null;

  const isFormValid = useMemo(
    () =>
      drafts.every(draft =>
        DAILY_NAV_THRESHOLD_KEYS.every(
          key => draft.thresholds[key] === '' || isValidThresholdPercent(draft.thresholds[key])
        )
      ),
    [drafts]
  );

  const dirtyRows = useMemo(
    () => drafts.filter((draft, index) => !draftsEqual(draft, savedDrafts[index] ?? draft)),
    [drafts, savedDrafts]
  );
  const isDirty = dirtyRows.length > 0;
  const applyDialogCompany = applyDialog?.companyId
    ? (drafts.find(draft => draft.companyId === applyDialog.companyId) ?? null)
    : null;
  const dialogCanApply = applyDialogCompany ? applyDialogCompany.canApply : canApplyNow;
  let applyDialogTitle = 'Daily NAV company configuration saved';
  if (applyDialogCompany) {
    applyDialogTitle = `Apply these settings to ${applyDialogCompany.companyName}'s open NAV day?`;
  } else if (dialogCanApply) {
    applyDialogTitle = 'Saved — apply to the open NAV day?';
  }
  const applyRecordCountLabel = `${recordCount} record${recordCount === 1 ? '' : 's'}`;
  const applyDialogBody = applyDialogCompany
    ? `Applying now also re-points ${applyDialogCompany.companyName}'s record for ${dailyNavDateLabel(applyDialogCompany.appliedNavDate)}, the day still in progress. This cannot be undone.`
    : `Applying now also re-points ${applyRecordCountLabel} for ${dailyNavApplyDatesLabel(applicableDates)}, the day still in progress. This cannot be undone.`;

  const rowApplyDisabledReason = (draft: CompanyDraft) =>
    dailyNavApplyDisabledReason({
      isDisabled: !canEdit || isApplyPending || isDirty || !draft.canApply,
      isDirty,
      hasNoOpenNavDay: !draft.appliedNavDate,
      hasNothingToApply: Boolean(draft.appliedNavDate) && !draft.canApply && !draft.applyBlockedReason,
      blockedDate: draft.applyBlockedReason
        ? { blocked_reason: draft.applyBlockedReason, session_close: draft.applySessionClose }
        : null,
    });

  const updateDraft = (companyId: number, updater: (draft: CompanyDraft) => CompanyDraft) => {
    setDrafts(prevState => prevState.map(draft => (draft.companyId === companyId ? updater(draft) : draft)));
  };

  const saveCompanyConfigs = useCallback(async () => {
    if (!firmId || !canEdit || !isFormValid || !isDirty) {
      return;
    }
    const updated = await updateFirmDailyNavCompanyConfigs(firmId, {
      companies: dirtyRows.map(draftToRowPayload),
    });
    if (!updated) {
      return;
    }
    enqueueSnackbar('Daily NAV company configuration saved', { variant: 'success' });
    await refetchApplyPreview();
    setApplyDialog({ versionId: updated.firm_config?.id ?? null });
  }, [
    firmId,
    canEdit,
    isFormValid,
    isDirty,
    dirtyRows,
    updateFirmDailyNavCompanyConfigs,
    refetchApplyPreview,
    enqueueSnackbar,
  ]);

  const handleConfirmApply = useCallback(async () => {
    if (!firmId || applyVersionId === null) {
      return;
    }
    const companyId = applyDialog?.companyId;
    const isCompanyScoped = companyId !== undefined;
    setApplyDialog(null);
    const response = await applyDailyNavConfig(firmId, applyVersionId, companyId);
    if (!response) {
      return;
    }
    if (!response.reanchored_count) {
      const refusal = dailyNavApplyRefusalMessage(response.dates);
      enqueueSnackbar(
        refusal ||
          (isCompanyScoped
            ? 'Nothing needed applying — this record already reflects the open config'
            : 'Nothing needed applying — records already reflect the open config'),
        { variant: refusal ? 'warning' : 'info' }
      );
      return;
    }
    enqueueSnackbar(
      isCompanyScoped
        ? 'Re-pointed this company’s record onto the open config'
        : `Re-pointed ${response.reanchored_count} record${response.reanchored_count === 1 ? '' : 's'} onto the open config`,
      { variant: 'success' }
    );
  }, [firmId, applyVersionId, applyDialog?.companyId, applyDailyNavConfig, enqueueSnackbar]);

  const secondaryNavigation = useMemo(
    () => ({
      title: FIRM_SETTINGS,
      items: buildFirmSettingsNavigationItems(firmSlug, { dailyNavEnabled: true }),
      actions: hasFirmEditAccess
        ? [
          {
            type: 'button' as const,
            key: 'firm-daily-nav-companies-save',
            label: 'Save',
            variant: 'positive' as const,
            onClick: saveCompanyConfigs,
            disabled: !firmId || !canEdit || !canSaveReady(isFormValid, isDirty, isPending),
          },
        ]
        : [],
    }),
    [firmSlug, hasFirmEditAccess, saveCompanyConfigs, firmId, canEdit, isFormValid, isDirty, isPending]
  );

  useSecondaryNavigationRegistration(secondaryNavigation);

  // Settings / Companies / Setup wizard: the Daily NAV pages share one tertiary tab strip.
  const tertiaryNavigation = useMemo(
    () => ({ ariaLabel: 'Daily NAV', items: buildDailyNavTertiaryNavigationItems(firmSlug), value: DAILY_NAV_COMPANIES_TAB }),
    [firmSlug]
  );
  useTertiaryNavigationRegistration(tertiaryNavigation);

  const alexandriaDraft = drafts.find(draft => draft.companyId === alexandriaCompanyId) ?? null;

  if (isLoading) {
    return <GridSkeleton />;
  }

  return (
    <Box sx={{ mb: 10, pt: 1 }}>
      <Typography variant="h6" gutterBottom>
        {DAILY_NAV_COMPANIES_PAGE_TITLE}
      </Typography>
      <DailyNavSetupBanner firmId={firmId} firmSlug={firmSlug} />
      <Typography variant="body2" sx={{ mb: 3 }}>
        Enable Daily NAV per company, override alert thresholds, and link the Alexandria profile. Empty threshold cells
        inherit the firm default.
        {firmSlug && (
          <>
            {' '}
            <Link component={RouterLink} to={firmDailyNavSettingsUrl(firmSlug)}>
              Edit firm defaults
            </Link>
            .
          </>
        )}
      </Typography>
      {!hasSavedFirmConfig && (
        <Alert severity="info" sx={{ mb: 3 }} data-testid={DAILY_NAV_COMPANIES_TEST_IDS.noFirmConfigAlert}>
          This firm has not saved a Daily NAV template yet. Company configuration can&apos;t be saved until the firm
          saves its Daily NAV Settings.
        </Alert>
      )}
      <Paper>
        <Table size="small" data-testid={DAILY_NAV_COMPANIES_TEST_IDS.table}>
          <TableHead>
            <TableRow>
              <TableCell>Company</TableCell>
              <TableCell>NAV day</TableCell>
              <TableCell>Enabled</TableCell>
              {DAILY_NAV_THRESHOLD_KEYS.map(key => (
                <TableCell key={key}>{THRESHOLD_COLUMN_LABELS[key]}</TableCell>
              ))}
              <TableCell>Alexandria</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {drafts.map(draft => (
              <TableRow key={draft.companyId}>
                <TableCell>
                  {firmSlug && draft.companySlug ? (
                    <Link
                      component={RouterLink}
                      to={companyDailyNavSettingsUrl(firmSlug, draft.companySlug)}
                      data-testid={DAILY_NAV_COMPANIES_TEST_IDS.companyLink(draft.companyId)}>
                      {draft.companyName}
                    </Link>
                  ) : (
                    draft.companyName
                  )}
                </TableCell>
                <TableCell>
                  <CompanyNavDayCell
                    draft={draft}
                    nextNavDate={nextNavDate}
                    showApply={hasFirmEditAccess && Boolean(draft.appliedNavDate) && !isOpenConfigCurrent(draft)}
                    applyDisabled={!canEdit || isApplyPending || isDirty || !draft.canApply}
                    applyDisabledReason={rowApplyDisabledReason(draft)}
                    onApply={() =>
                      setApplyDialog({
                        versionId: firmConfig?.id ?? null,
                        companyId: draft.companyId,
                        companyName: draft.companyName,
                      })
                    }
                  />
                </TableCell>
                <TableCell>
                  <Checkbox
                    size="small"
                    checked={draft.monitoringEnabled}
                    disabled={!canEdit}
                    onChange={event =>
                      updateDraft(draft.companyId, current => ({
                        ...current,
                        monitoringEnabled: event.target.checked,
                      }))
                    }
                    inputProps={{
                      'aria-label': `Enable Daily NAV for ${draft.companyName}`,
                    }}
                    data-testid={DAILY_NAV_COMPANIES_TEST_IDS.enabledCheckbox(draft.companyId)}
                  />
                </TableCell>
                {DAILY_NAV_THRESHOLD_KEYS.map(key => {
                  const groupField = thresholdGroups.flatMap(group => group.fields).find(field => field.key === key);
                  const inherited = fractionToPercent(firmConfig?.[key]);
                  return (
                    <TableCell key={key} sx={{ minWidth: 96 }}>
                      <TextField
                        size="small"
                        value={draft.thresholds[key]}
                        placeholder={inherited ? `${inherited}%` : 'Inherit'}
                        disabled={!canEdit}
                        error={draft.thresholds[key] !== '' && !isValidThresholdPercent(draft.thresholds[key])}
                        onChange={event =>
                          updateDraft(draft.companyId, current => ({
                            ...current,
                            thresholds: { ...current.thresholds, [key]: event.target.value },
                          }))
                        }
                        inputProps={{
                          'aria-label': `${draft.companyName} ${groupField?.label ?? key}`,
                        }}
                        data-testid={DAILY_NAV_COMPANIES_TEST_IDS.thresholdInput(draft.companyId, key)}
                      />
                    </TableCell>
                  );
                })}
                <TableCell>
                  <Button
                    size="small"
                    variant="text"
                    disabled={!canEdit}
                    onClick={() => setAlexandriaCompanyId(draft.companyId)}
                    data-testid={DAILY_NAV_COMPANIES_TEST_IDS.alexandriaButton(draft.companyId)}>
                    {draft.alexandriaId ? 'Linked' : 'Set'}
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {!drafts.length && (
              <TableRow>
                <TableCell colSpan={DAILY_NAV_THRESHOLD_KEYS.length + 4}>
                  <Typography variant="body2">This firm has no companies yet.</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>
      <Dialog
        open={alexandriaCompanyId !== null}
        onClose={() => setAlexandriaCompanyId(null)}
        data-testid={DAILY_NAV_COMPANIES_TEST_IDS.alexandriaDialog}>
        <DialogTitle>Alexandria company{alexandriaDraft ? ` — ${alexandriaDraft.companyName}` : ''}</DialogTitle>
        <DialogContent>
          <AlexandriaCompanyPicker
            firmId={firmId}
            companyId={alexandriaCompanyId}
            selectedAlexandriaId={alexandriaDraft?.alexandriaId ?? null}
            disabled={!canEdit}
            onChange={alexandriaId => {
              if (alexandriaCompanyId === null) {
                return;
              }
              updateDraft(alexandriaCompanyId, current => ({ ...current, alexandriaId }));
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAlexandriaCompanyId(null)}>Done</Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={applyDialog !== null}
        onClose={() => setApplyDialog(null)}
        data-testid={DAILY_NAV_COMPANIES_TEST_IDS.applyConfirmDialog}>
        <DialogTitle>{applyDialogTitle}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">{dailyNavNextDayLabel(nextNavDate)}.</Typography>
          {dialogCanApply ? (
            <Typography variant="body2" sx={{ mt: 1 }}>
              {applyDialogBody}
            </Typography>
          ) : (
            applyUnavailableReason && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {applyUnavailableReason}
              </Typography>
            )
          )}
        </DialogContent>
        <DialogActions>
          {dialogCanApply ? (
            <>
              <Button onClick={() => setApplyDialog(null)}>Next NAV day only</Button>
              <Button
                variant="contained"
                onClick={handleConfirmApply}
                disabled={isApplyPending || applyVersionId === null || isApplyPreviewLoading}
                data-testid={DAILY_NAV_COMPANIES_TEST_IDS.applyConfirmButton}>
                {DAILY_NAV_APPLY_BUTTON_LABEL}
              </Button>
            </>
          ) : (
            <Button variant="contained" onClick={() => setApplyDialog(null)}>
              Done
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const canSaveReady = (isFormValid: boolean, isDirty: boolean, isPending: boolean) =>
  isFormValid && isDirty && !isPending;

export default DailyNavCompanies;
